/**
 * The two things every piece of clinic-authored content needs: how to choose a language
 * for it, and how to give a draft row an identity while it is being typed into.
 *
 * Both started life in ./serviceCatalog and moved here when the About page came to need
 * them too. They are about the convention rather than about either page: a clinic writes
 * Romanian and may or may not write English, and an editor holds rows that do not exist
 * on the server yet.
 */

/**
 * The label to show, given the active i18n language.
 *
 * Falls back to Romanian whenever there is no English to show -- an untranslated line
 * reading in the wrong language beats a blank one, and Romanian is the language every
 * clinic on the platform actually writes.
 */
export const localised = (ro: string, en: string | null | undefined, language: string): string => {
    const isEnglish: boolean = language.split('-')[0] === 'en';

    if (isEnglish && en && en.trim().length > 0) {
        return en;
    }

    return ro;
};

/**
 * Distinct per node and stable for as long as the draft lives.
 *
 * React needs a stable identity for a row the user is typing into, and a brand new row
 * has no server id to offer -- so drafts carry one of these instead of, or alongside, an
 * id.
 */
export const newKey = (): string =>
    // randomUUID needs a secure context; jsdom and plain-HTTP dev servers have neither.
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `k${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
